import React, { useMemo, useState } from 'react';

// material-ui
import {
  CardActions,
  CardContent,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';

// project imports
import MainCard from '../common/ui-component/cards/MainCard';
import SkeletonPopularCard from '../common/ui-component/cards/Skeleton/PopularCard';
import GlobalLoader from '../common/components/loader/GlobalLoader';
import SectionSkeleton from './SectionSkeleton';

// assets
import { gridSpacing } from '../../common/themes/constants';
import MyTextField from '../common/components/MyTextField';
import { useQuery } from '@apollo/client';
import clientGraphql from '../../common/apollo-graphql';
import { GET_ADMIN_REFERRAL_COUNTS_BY_CLINIC } from '../referrals/gql';

// ==============================|| DASHBOARD DEFAULT - POPULAR CARD ||============================== //

const months = [
  {
    value: 0,
    label: 'January',
  },
  {
    value: 1,
    label: 'February',
  },
  {
    value: 2,
    label: 'March',
  },
  {
    value: 3,
    label: 'April',
  },
  {
    value: 4,
    label: 'May',
  },
  {
    value: 5,
    label: 'June',
  },
  {
    value: 6,
    label: 'July',
  },
  {
    value: 7,
    label: 'August',
  },
  {
    value: 8,
    label: 'September',
  },
  {
    value: 9,
    label: 'October',
  },
  {
    value: 10,
    label: 'November',
  },
  {
    value: 11,
    label: 'December',
  },
];

const years = [
  {
    value: (new Date()).getFullYear(),
    label: "" + ((new Date()).getFullYear()),
  },
  {
    value: (new Date()).getFullYear() - 1,
    label: "" + ((new Date()).getFullYear() - 1),
  },
  {
    value: (new Date()).getFullYear() - 2,
    label: "" + ((new Date()).getFullYear() - 2),
  }
];


export default function TotalReferralsByMonth({ isLoading }) {
  const [value, setValue] = useState((new Date()).getMonth());
  const [valueYear, setValueYear] = useState((new Date()).getFullYear());
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total');
  const [sortDirection, setSortDirection] = useState('desc');

  const monthValue = "" + (value + 1) + "";
  const yearValue = "" + valueYear + "";

  const { data: dataClinicCounts, loading: loadingClinicCounts } = useQuery(GET_ADMIN_REFERRAL_COUNTS_BY_CLINIC, {
    client: clientGraphql,
    variables: {
      month: monthValue,
      year: yearValue,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const clinicRows = useMemo(() => {
    const rows = (dataClinicCounts?.admin_referral_counts_by_clinic || []).map((clinic) => {
      const sent = clinic.sent_count ?? 0;
      const received = clinic.received_count ?? 0;
      return {
        id: clinic.clinic_id,
        name: clinic.clinic_name,
        sent,
        received,
        total: sent + received,
      };
    });

    const filtered = search
      ? rows.filter((row) => row.name.toLowerCase().includes(search.toLowerCase()))
      : rows;

    const direction = sortDirection === 'asc' ? 1 : -1;
    return filtered.sort((a, b) => {
      if (a[sortBy] < b[sortBy]) return -1 * direction;
      if (a[sortBy] > b[sortBy]) return 1 * direction;
      return a.name.localeCompare(b.name);
    });
  }, [dataClinicCounts, search, sortBy, sortDirection]);

  const totalFromRows = useMemo(
    () => clinicRows.reduce((sum, row) => sum + (row.sent || 0), 0),
    [clinicRows]
  );

  const showTableSkeleton =
    loadingClinicCounts && !(dataClinicCounts?.admin_referral_counts_by_clinic || []).length;

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortBy(key);
    setSortDirection('desc');
  };

  return (
    <>
      {isLoading ? (
        <SkeletonPopularCard />
      ) : (
        <MainCard content={false}>
          {(loadingClinicCounts && !showTableSkeleton) && <GlobalLoader />}
          <CardContent>
            <Grid container spacing={gridSpacing}>
              <Grid item xs={12}>
                <Grid container alignContent="center" justifyContent="space-between">
                  <Grid item>
                    <Grid container direction="column" spacing={1}>
                      <Grid item>
                        <Typography variant="subtitle2">Total Referrals</Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="h3">{totalFromRows}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <MyTextField
                      id="standard-select-currency"
                      select
                      value={value}
                      onChange={e => {
                        setValue(e.target.value);
                      }
                    }
                    >
                      {months.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </MyTextField>
                    <MyTextField
                      id="standard-select-year"
                      select
                      value={valueYear}
                      onChange={e => {
                        setValueYear(e.target.value);
                      }
                    }
                    >
                      {years.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </MyTextField>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search clinics"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                {showTableSkeleton ? (
                  <SectionSkeleton height={400} />
                ) : (
                  <>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <TableSortLabel
                                active={sortBy === 'name'}
                                direction={sortBy === 'name' ? sortDirection : 'asc'}
                                onClick={() => handleSort('name')}
                              >
                                Clinic
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={sortBy === 'sent'}
                                direction={sortBy === 'sent' ? sortDirection : 'asc'}
                                onClick={() => handleSort('sent')}
                              >
                                Sent
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={sortBy === 'received'}
                                direction={sortBy === 'received' ? sortDirection : 'asc'}
                                onClick={() => handleSort('received')}
                              >
                                Received
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={sortBy === 'total'}
                                direction={sortBy === 'total' ? sortDirection : 'asc'}
                                onClick={() => handleSort('total')}
                              >
                                Total
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clinicRows.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.name}</TableCell>
                              <TableCell align="right">{row.sent}</TableCell>
                              <TableCell align="right">{row.received}</TableCell>
                              <TableCell align="right">{row.total}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {clinicRows.length === 0 && (
                      <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                        No clinics found
                      </Typography>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </CardContent>
          <CardActions sx={{ p: 1.25, pt: 0, justifyContent: 'center' }}>
            
          </CardActions>
        </MainCard>
      )}
    </>
  );
}
